import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from '@tanstack/react-router'
import { toast } from 'sonner'
import { useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import { InviteDirectReportButton } from '@/features/direct-reports'
import {
  AuthGuard,
  useCurrentOrganizationId,
  useMyConvexUserId,
  useMyConvexOrganizationId,
} from '@/features/auth'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

export const Route = createFileRoute('/(dashboard)/direct-reports')({
  component: RouteComponent,
})

function RouteComponent() {
  const currentOrgId = useCurrentOrganizationId()
  const location = useLocation()
  const convexUserId = useMyConvexUserId()
  const convexOrgId = useMyConvexOrganizationId()

  const invites = useQuery(
    api.invites.listByManager,
    convexUserId && convexOrgId
      ? {
          managerId: convexUserId as Id<'users'>,
          organizationId: convexOrgId as Id<'organizations'>,
        }
      : 'skip',
  )
  const hasPendingInvites = (invites ?? []).some(
    (inv) => inv.status.toLowerCase() === 'pending',
  )

  // Only show tabs on main DirectReports routes, not on detail routes
  const isMainRoute =
    location.pathname === '/direct-reports' ||
    location.pathname === '/direct-reports/' ||
    location.pathname === '/direct-reports/invited' ||
    location.pathname === '/direct-reports/invited/'

  // Determine active tab based on current pathname
  const getActiveTab = () => {
    if (
      location.pathname === '/direct-reports/invited' ||
      location.pathname === '/direct-reports/invited/'
    ) {
      return 'invited'
    }
    return 'accepted'
  }

  const activeTab = getActiveTab()

  if (!isMainRoute) {
    // Detail routes handle their own AuthGuard
    return <Outlet />
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: currentOrgId || '',
        role: ['admin', 'manager'],
      }}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Direct Reports
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Here's what's happening with your direct reports.
            </p>
          </div>
          <InviteDirectReportButton
            onSuccess={() => toast.success('Invitation sent successfully!')}
          />
        </div>
        {hasPendingInvites && (
          <Tabs value={activeTab}>
            <TabsList className="grid w-full max-w-xs grid-cols-2">
              <TabsTrigger value="accepted" asChild>
                <Link to="/direct-reports">Accepted</Link>
              </TabsTrigger>
              <TabsTrigger value="invited" asChild>
                <Link to="/direct-reports/invited">Invited</Link>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <Outlet />
      </div>
    </AuthGuard>
  )
}
